import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import Shell from "@/components/Shell";
import { Store, Package, BadgeCheck, Check, Loader2, BookOpen, ShoppingCart, Banknote, MapPin } from "lucide-react";

function embedUrl(url) {
  if (!url) return null;
  const m = (url || "").match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]+)/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
}

function PhysicalStore({ ctx, user }) {
  const [vendors, setVendors] = useState([]);
  const [nationwide, setNationwide] = useState(false);
  const [vendorId, setVendorId] = useState("");
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState({});
  const [open, setOpen] = useState(false);
  const [cust, setCust] = useState({ customer_name: user?.name || "", customer_phone: "", delivery_address: "" });
  const [receipt, setReceipt] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const scoped = ctx?.location_configured;
    const params = scoped ? { country_code: ctx.country_code || undefined, state: ctx.state || undefined, district: ctx.district || undefined } : {};
    api.get("/store/vendors", { params }).then(r => {
      if (scoped && r.data.length === 0) {
        // No local stores → fall back to nationwide.
        setNationwide(true);
        api.get("/store/vendors").then(r2 => { setVendors(r2.data); setVendorId(r2.data[0]?.vendor_id || ""); });
      } else {
        setNationwide(!scoped);
        setVendors(r.data); setVendorId(r.data[0]?.vendor_id || "");
      }
    }).catch(() => {});
  }, [ctx]);

  useEffect(() => {
    if (!vendorId) { setProducts([]); return; }
    const params = { vendor_id: vendorId };
    if (ctx?.grade) params.q = undefined;
    api.get("/store/products", { params }).then(r => setProducts(r.data)).catch(() => {});
  }, [vendorId]);

  const add = (p) => setCart({ ...cart, [p.product_id]: { product: p, qty: (cart[p.product_id]?.qty || 0) + 1 } });
  const remove = (id) => { const c = { ...cart }; delete c[id]; setCart(c); };
  const items = Object.values(cart);
  const total = useMemo(() => items.reduce((s, x) => s + x.product.selling_price * x.qty, 0), [cart]);
  const vendor = vendors.find(v => v.vendor_id === vendorId);

  const placeCod = async () => {
    if (!cust.customer_name || !cust.customer_phone) { toast.error("Name and phone required"); return; }
    setBusy(true);
    try {
      const r = await api.post("/store/checkout", {
        vendor_id: vendorId, items: items.map(x => ({ product_id: x.product.product_id, qty: x.qty })),
        ...cust, fulfillment: "DELIVERY", country_code: vendor?.country_code || "IN",
      });
      setReceipt(r.data); setCart({}); setOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Order failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      {nationwide && (
        <div className="nb-card bg-[#FDE047] p-3 text-sm flex items-center gap-2" data-testid="kit-nationwide-banner">
          <MapPin size={16} /> Showing all available stores — school location not configured.
        </div>
      )}
      <div className="nb-card bg-[#93C5FD] p-3 text-sm flex items-center gap-2">
        <Banknote size={16} /> <b>Cash on Delivery only.</b> You'll get a 4-digit PIN — show it to the delivery person to confirm receipt.
      </div>
      <div className="flex gap-2 flex-wrap items-center">
        <span className="text-xs font-bold uppercase opacity-60">Store</span>
        <select className="nb-select" value={vendorId} onChange={e => setVendorId(e.target.value)} data-testid="kit-vendor-select">
          {vendors.map(v => <option key={v.vendor_id} value={v.vendor_id}>{v.business_name} · {v.city}</option>)}
          {vendors.length === 0 && <option value="">No stores yet</option>}
        </select>
        <button className="nb-btn ml-auto" onClick={() => items.length ? setOpen(true) : toast.info("Cart is empty")} data-testid="kit-cart-btn">
          <ShoppingCart size={16} /> {items.length > 0 && <span className="nb-badge bg-[#FDE047] ml-1">{items.reduce((s, x) => s + x.qty, 0)}</span>}
        </button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {products.map(p => (
          <div key={p.product_id} className="nb-card bg-white p-4 flex flex-col" data-testid={`kit-product-${p.product_id}`}>
            <div className="h-28 bg-[#FAF7F0] border-2 border-black rounded grid place-items-center mb-2 overflow-hidden">
              {p.image_url ? <img src={p.image_url} alt={p.title} className="w-full h-full object-cover" /> : <Package size={30} className="opacity-40" />}
            </div>
            <div className="font-bold text-sm flex-1">{p.title}</div>
            <div className="text-xs opacity-60">{p.category_code}{p.grade_tag && ` · ${p.grade_tag}`}</div>
            <div className="display text-lg font-black mono mt-1">₹{p.selling_price}</div>
            <button className="nb-btn text-xs mt-2 w-full justify-center" disabled={p.stock_qty <= 0} onClick={() => add(p)} data-testid={`kit-add-${p.product_id}`}>
              {p.stock_qty > 0 ? "Add to Cart" : "Out of stock"}
            </button>
          </div>
        ))}
        {products.length === 0 && <div className="col-span-full text-center py-10 text-slate-500">No products in this store yet.</div>}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={() => setOpen(false)}>
          <div className="nb-card bg-white p-5 max-w-md w-full space-y-3" onClick={e => e.stopPropagation()} data-testid="kit-checkout-modal">
            <h3 className="display text-lg font-bold">Cash on Delivery · {vendor?.business_name}</h3>
            {items.map(x => (
              <div key={x.product.product_id} className="flex justify-between text-sm">
                <span>{x.product.title} × {x.qty}</span>
                <span className="flex items-center gap-2 mono">₹{(x.product.selling_price * x.qty).toFixed(2)}
                  <button className="text-red-600 text-xs" onClick={() => remove(x.product.product_id)}>✕</button></span>
              </div>
            ))}
            <div className="flex justify-between font-bold border-t-2 border-black pt-2 mono">Pay on delivery <span data-testid="kit-total">₹{total.toFixed(2)}</span></div>
            <input className="nb-input" placeholder="Full Name *" value={cust.customer_name} onChange={e => setCust({ ...cust, customer_name: e.target.value })} data-testid="kit-name" />
            <input className="nb-input mono" placeholder="Phone *" value={cust.customer_phone} onChange={e => setCust({ ...cust, customer_phone: e.target.value })} data-testid="kit-phone" />
            <textarea className="nb-input" rows="2" placeholder="Delivery Address" value={cust.delivery_address} onChange={e => setCust({ ...cust, delivery_address: e.target.value })} data-testid="kit-address" />
            <button className="nb-btn w-full justify-center" onClick={placeCod} disabled={busy} data-testid="kit-place-order">
              {busy ? <Loader2 size={16} className="animate-spin" /> : `Place COD Order · ₹${total.toFixed(2)}`}
            </button>
          </div>
        </div>
      )}
      {receipt && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={() => setReceipt(null)}>
          <div className="nb-card bg-[#86EFAC] p-6 max-w-md w-full space-y-3 text-center" onClick={e => e.stopPropagation()} data-testid="kit-receipt">
            <div className="w-14 h-14 rounded-full bg-white border-2 border-black grid place-items-center mx-auto"><Check size={28} /></div>
            <h3 className="display text-2xl font-black">Order Placed!</h3>
            <p className="text-sm">Show this 4-digit PIN to the delivery person on handover:</p>
            <div className="display text-5xl font-black mono tracking-widest bg-white border-2 border-black rounded p-3" data-testid="kit-receipt-otp">{receipt.delivery_otp}</div>
            <div className="text-xs mono">Order {receipt.order_id.slice(0, 8)} · Pay ₹{receipt.total} on delivery</div>
            <button className="nb-btn w-full justify-center" onClick={() => setReceipt(null)}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
}

function DigitalStore({ ctx }) {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  useEffect(() => {
    api.get("/tv/content", { params: { board: ctx?.board_code || undefined, grade: ctx?.grade || undefined } })
      .then(r => setItems((r.data || []).filter(c => c.content_type === "NOTE" || c.content_type === "COURSE"))).catch(() => {});
  }, [ctx]);
  const getAccess = async (c) => {
    try { await api.post(`/tv/content/${c.content_id}/purchase`, { amount: 1, currency: "INR" }); } catch (e) {}
    setActive(c); toast.success("Access granted");
  };
  return (
    <div className="space-y-4">
      <div className="nb-card bg-[#C4B5FD] p-3 text-sm">Courses, notes & cheat-sheets from approved content providers — matched to <b>{ctx?.board_code || "your board"} · {ctx?.grade || "your grade"}</b>. Instant access on checkout.</div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(c => (
          <div key={c.content_id} className="nb-card bg-white p-4 flex flex-col" data-testid={`digital-${c.content_id}`}>
            <div className="h-28 bg-[#C4B5FD] border-2 border-black rounded grid place-items-center mb-2 overflow-hidden">
              {c.thumbnail_url ? <img src={c.thumbnail_url} alt={c.title} className="w-full h-full object-cover" /> : <BookOpen size={30} />}
            </div>
            <div className="font-bold text-sm flex-1">{c.title}</div>
            <div className="text-xs opacity-60">{c.board} {c.grade} · {c.subject}</div>
            <span className="nb-badge bg-[#86EFAC] text-[10px] mt-1 inline-block w-fit">{c.content_type}</span>
            <button className="nb-btn text-xs mt-2 w-full justify-center" onClick={() => getAccess(c)} data-testid={`digital-access-${c.content_id}`}>Get Access</button>
          </div>
        ))}
        {items.length === 0 && <div className="col-span-full text-center py-10 text-slate-500">No digital study material for your grade yet.</div>}
      </div>
      {active && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4" onClick={() => setActive(null)}>
          <div className="max-w-3xl w-full space-y-2 nb-card bg-white p-4" onClick={e => e.stopPropagation()} data-testid="digital-viewer">
            {embedUrl(active.external_url)
              ? <div className="aspect-video"><iframe title={active.title} src={embedUrl(active.external_url)} className="w-full h-full rounded" allowFullScreen /></div>
              : active.media_url ? <video src={active.media_url} controls className="w-full rounded" />
                : <div className="p-6 text-sm">{active.description || "This resource is now unlocked in your library."}</div>}
            <div className="flex items-center justify-between"><div className="font-bold">{active.title}</div><button className="nb-btn nb-btn-danger text-xs" onClick={() => setActive(null)}>Close</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

export function KitStorePanel() {
  const { user } = useAuth();
  const [ctx, setCtx] = useState(null);
  const [sub, setSub] = useState("physical");
  useEffect(() => { api.get("/student/discovery-context").then(r => setCtx(r.data)).catch(() => setCtx({})); }, []);
  const subs = [
    { id: "physical", label: "Storez · Physical", icon: Store },
    { id: "digital", label: "Digital Store", icon: BookOpen },
  ];
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <Store size={20} /><h2 className="display text-2xl font-bold">Kit Store</h2>
        {ctx?.school_name && <span className="nb-badge bg-white text-xs">{ctx.school_name}</span>}
      </div>
      <div className="flex gap-2">
        {subs.map(s => (
          <button key={s.id} onClick={() => setSub(s.id)} data-testid={`kit-subtab-${s.id}`}
            className={`nb-btn text-xs ${sub === s.id ? "" : "nb-btn-ghost"}`}><s.icon size={14} /> {s.label}</button>
        ))}
      </div>
      {ctx && sub === "physical" && <PhysicalStore ctx={ctx} user={user} />}
      {ctx && sub === "digital" && <DigitalStore ctx={ctx} />}
    </div>
  );
}

export default function StudentKitStore() {
  const { user } = useAuth();
  return (
    <Shell title="Kit Store" subtitle={user?.name}>
      <KitStorePanel />
    </Shell>
  );
}
